import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, Form, FormGroup, Label, Input, Spinner, Badge } from "reactstrap";
import CorsApi from "../../Api/CorsApi";
import { Toast } from "primereact/toast";
import "./CorsConfig.scss";

const CorsConfig = () => {
  const [corsConfigs, setCorsConfigs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalConfigs, setOriginalConfigs] = useState([]);
  const [newConfig, setNewConfig] = useState({
    origin: '',
    methods: []
  });
  const [newMethod, setNewMethod] = useState('');
  const [editingMethods, setEditingMethods] = useState({}); // Track editing methods per config

  const toast = React.useRef(null);

  const availableMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'];

  useEffect(() => {
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    setLoading(true);
    try {
      const response = await CorsApi.getCors();
      if (response.data.success || response.data.status) {
        const corsData = response.data.data || response.data;
        if (Array.isArray(corsData)) {
          // Ensure methods is always an array
          const processedConfigs = corsData.map(corsConfig => ({
            ...corsConfig,
            methods: Array.isArray(corsConfig.methods)
              ? corsConfig.methods
              : (typeof corsConfig.methods === 'string' && corsConfig.methods)
                ? [corsConfig.methods]
                : []
          }));
          setCorsConfigs(processedConfigs);
          setOriginalConfigs(processedConfigs);
        } else {
          setCorsConfigs([]);
          setOriginalConfigs([]);
        }
      }
    } catch (error) {
      console.error('Error fetching CORS config:', error);
      showToast('error', 'Error', 'Failed to fetch CORS configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setCorsConfigs(originalConfigs);
    setIsEditing(false);
    setNewConfig({ origin: '', methods: [] });
    setNewMethod('');
    setEditingMethods({});
  };



  const showToast = (severity, summary, detail) => {
    if (toast.current) {
      toast.current.show({ severity, summary, detail });
    }
  };

  const handleConfigChange = async (index, field, value) => {
    const configToUpdate = corsConfigs[index];
    const updatedConfig = {
      ...configToUpdate,
      [field]: field === 'methods' ? value : value // Always send as array
    };
    
    setSaving(true);
    try {
      await CorsApi.updateCors(configToUpdate._id, updatedConfig);
      // Refresh the data
      await fetchSiteConfig();
      showToast('success', 'Success', 'CORS configuration updated successfully');
    } catch (error) {
      console.error('Error updating CORS config:', error);
      showToast('error', 'Error', 'Failed to update CORS configuration');
    } finally {
      setSaving(false);
    }
  };

  const addNewConfig = async () => {
    if (newConfig.origin && newConfig.methods.length > 0) {
      setSaving(true);
      try {
        const configToAdd = {
          origin: newConfig.origin,
          methods: newConfig.methods // Always send as array
        };
        
        const response = await CorsApi.addCors(configToAdd);
        if (response.data.success || response.data.status) {
          // Refresh the data to get the updated list
          await fetchSiteConfig();
          setNewConfig({ origin: '', methods: [] });
          setNewMethod('');
          setEditingMethods({});
          showToast('success', 'Success', 'CORS configuration added successfully');
        }
      } catch (error) {
        console.error('Error adding CORS config:', error);
        showToast('error', 'Error', 'Failed to add CORS configuration');
      } finally {
        setSaving(false);
      }
    }
  };

  const removeConfig = async (index) => {
    const configToRemove = corsConfigs[index];
    setSaving(true);
    try {
      await CorsApi.deleteCors(configToRemove._id);
      // Refresh the data
      await fetchSiteConfig();
      showToast('success', 'Success', 'CORS configuration removed successfully');
    } catch (error) {
      console.error('Error removing CORS config:', error);
      showToast('error', 'Error', 'Failed to remove CORS configuration');
    } finally {
      setSaving(false);
    }
  };

  const addMethodToNew = () => {
    if (newMethod && !newConfig.methods.includes(newMethod)) {
      setNewConfig(prev => ({
        ...prev,
        methods: [...prev.methods, newMethod]
      }));
      setNewMethod('');
    }
  };
  const removeMethodFromNew = (method) => {
    setNewConfig(prev => ({
      ...prev,
      methods: prev.methods.filter(m => m !== method)
    }));
  };
  const handleEditingMethodChange = (configIndex, value) => {
    setEditingMethods(prev => ({ ...prev, [configIndex]: value }));
  };
  const addMethodToConfig = (configIndex) => {
    const method = editingMethods[configIndex];
    if (method && !corsConfigs[configIndex].methods.includes(method)) {
      const updatedMethods = [...corsConfigs[configIndex].methods, method];
      handleConfigChange(configIndex, 'methods', updatedMethods);
      setEditingMethods(prev => ({ ...prev, [configIndex]: '' }));
    }
  };
  const removeMethodFromConfig = (configIndex, method) => {
    const updatedMethods = corsConfigs[configIndex].methods.filter(m => m !== method);
    handleConfigChange(configIndex, 'methods', updatedMethods);
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col md={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle className="h5 mb-0">CORS Configuration</CardTitle>
                {!isEditing && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                )}
              </div>

              <Form>
                {/* Existing CORS Configurations */}
                {corsConfigs.map((config, index) => (
                  <div key={index} className="cors-config-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">Configuration {index + 1}</h6>
                      {isEditing && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeConfig(index)}
                          disabled={saving}
                        >
                          {saving ? <Spinner size="sm" /> : 'Remove'}
                        </Button>
                      )}
                    </div>
                    
                    <FormGroup>
                      <Label>Origin</Label>
                      <Input
                        type="text"
                        value={config.origin}
                        onChange={(e) => handleConfigChange(index, 'origin', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Enter origin (e.g., https://example.com)"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Methods</Label>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        {config.methods.length > 0 ? (
                          config.methods.map((method, i) => (
                            <Badge color="primary" key={i} className="me-1">
                              {method}
                              {isEditing && (
                                <Button
                                  close
                                  size="sm"
                                  className="ms-1"
                                  onClick={() => removeMethodFromConfig(index, method)}
                                />
                              )}
                            </Badge>
                          ))
                        ) : (
                          <div className="text-muted">No methods selected</div>
                        )}
                      </div>
                      {isEditing && (
                        <div className="d-flex gap-2 mt-2">
                          <Input
                            type="text"
                            value={editingMethods[index] || ''}
                            onChange={(e) => handleEditingMethodChange(index, e.target.value)}
                            placeholder="Select method"
                            list={`methods-list-${index}`}
                          />
                          <datalist id={`methods-list-${index}`}>
                            {availableMethods.map(method => (
                              <option key={method} value={method} />
                            ))}
                          </datalist>
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={() => addMethodToConfig(index)}
                            disabled={!(editingMethods[index] && !config.methods.includes(editingMethods[index]))}
                          >
                            Add Method
                          </Button>
                        </div>
                      )}
                    </FormGroup>
                  </div>
                ))}

                {/* Add New Configuration */}
                {isEditing && (
                  <div className="cors-config-item mb-3 p-3 border rounded border-dashed">
                    <h6 className="mb-3">Add New Configuration</h6>
                    
                    <FormGroup>
                      <Label>Origin</Label>
                      <Input
                        type="text"
                        value={newConfig.origin}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, origin: e.target.value }))}
                        placeholder="Enter origin (e.g., https://example.com)"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Methods</Label>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        {newConfig.methods.length > 0 ? (
                          newConfig.methods.map((method, i) => (
                            <Badge color="success" key={i} className="me-1">
                              {method}
                              <Button
                                close
                                size="sm"
                                className="ms-1"
                                onClick={() => removeMethodFromNew(method)}
                              />
                            </Badge>
                          ))
                        ) : (
                          <div className="text-muted">No methods selected</div>
                        )}
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <Input
                          type="text"
                          value={newMethod}
                          onChange={(e) => setNewMethod(e.target.value)}
                          placeholder="Select method"
                          list="methods-list-new"
                        />
                        <datalist id="methods-list-new">
                          {availableMethods.map(method => (
                            <option key={method} value={method} />
                          ))}
                        </datalist>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={addMethodToNew}
                          disabled={!newMethod || newConfig.methods.includes(newMethod)}
                        >
                          Add Method
                        </Button>
                      </div>
                    </FormGroup>

                                         <Button
                       color="success"
                       size="sm"
                       onClick={addNewConfig}
                       disabled={saving || !newConfig.origin || newConfig.methods.length === 0}
                     >
                       {saving ? <Spinner size="sm" /> : 'Add Configuration'}
                     </Button>
                  </div>
                )}


              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CorsConfig;